import * as React from "react";
import { PresignedImage } from "@/components/common/PresignedImage";
import { Download, ExternalLink, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fileService } from "@/services/ServiceRegistry";
import { toast } from "sonner";

export interface DocumentItemProps {
  id: number;
  fileId?: string;
  type?: string;
  name?: string;
  className?: string;
  remove?: (id: number) => void;
}

function DocumentItem({
  id,
  name,
  remove,
  fileId,
  type,
  className,
}: DocumentItemProps) {
  const [isOpening, setIsOpening] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const getDocumentUrl = async () => {
    if (!fileId) {
      throw new Error("Document file is not available");
    }

    return fileService.getSignedUrl(fileId);
  };

  const handlePreview = async () => {
    try {
      setIsOpening(true);
      const signedUrl = await getDocumentUrl();
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Unable to open document");
    } finally {
      setIsOpening(false);
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const signedUrl = await getDocumentUrl();
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new Error("Unable to download document");
      }

      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name || `${type || "guest-document"}-${id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Unable to download document");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div key={id} className="relative">
      <button
        type="button"
        onClick={fileId ? handlePreview : undefined}
        disabled={!fileId || isOpening || isDownloading}
        aria-label={name ? `Preview ${name}` : "Preview document"}
        className={cn(
          "flex h-12 w-12 flex-col items-center justify-center rounded-lg border border-gray-300 bg-gray-100 p-1 disabled:cursor-default",
          fileId && "cursor-pointer hover:border-zinc-500",
          className,
        )}
      >
        {fileId && type ? (
          <>
            <PresignedImage
              fileId={fileId}
              alt={`${type} document`}
              className="h-full w-full bg-gray-50 object-contain"
            />
            {isOpening && (
              <Loader2 className="text-foreground absolute size-4 animate-spin" />
            )}
          </>
        ) : (
          <Upload className="mb-1 h-4 w-4 text-gray-400" />
        )}
        {name && (
          <span className="w-full truncate text-center text-xs text-gray-600">
            {name.split(".")[0].substring(0, 6)}
          </span>
        )}
      </button>
      {fileId && (
        <Button
          type="button"
          variant="new_ghost"
          size="icon_sm"
          className="absolute -bottom-2 -left-2 size-5 rounded-full bg-white shadow-sm"
          onClick={handleDownload}
          disabled={isOpening || isDownloading}
          aria-label={name ? `Download ${name}` : "Download document"}
        >
          {isDownloading ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Download className="size-3" />
          )}
        </Button>
      )}
      {fileId && !isOpening && !isDownloading && (
        <ExternalLink className="pointer-events-none absolute top-1 right-1 size-3 text-zinc-500" />
      )}
      {remove && (
        <Button
          type="button"
          size="sm"
          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-gray-600 p-0 text-white hover:bg-gray-800"
          onClick={() => remove(id)}
        >
          <X className="h-2 w-2" />
        </Button>
      )}
    </div>
  );
}

export { DocumentItem };
